/*
PUBLIC LOAN APPLICATION FORM
Simplified version for public users to submit quick loan applications
Based on QuickReferral but optimized for public-facing use
*/

import React, { useEffect, useState } from 'react';
import { Formik, Form, Field } from 'formik';
import PublicLoanApplicationSchema from './schema/PublicLoanApplicationSchema.json';
import PublicLoanApplicationValidationSchema from './schema/PublicLoanApplicationValidationSchema';
import { 
    FormSubmissionButton, 
    SelectField, 
    InputField, 
    TextArea, 
    MaskedField,
    CheckBox 
} from '../HtmlComponents';
import { SearchLocationInput } from '../../api/googleApi';
import './PublicLoanApplication.css';

const PublicLoanApplication = ({ onSubmit, isSubmitting = false, referrerInfo = null }) => {
    const [applicationData, setApplicationData] = useState(PublicLoanApplicationSchema);
    const [submitAttempted, setSubmitAttempted] = useState(false);

    // Ensure contact preferences have default values
    useEffect(() => {
        setApplicationData(prevData => ({
            ...prevData,
            contactPreferences: {
                ...prevData.contactPreferences,
                preferredContactMethod: prevData.contactPreferences.preferredContactMethod || 'Phone',
                preferredContactTimeFrom: prevData.contactPreferences.preferredContactTimeFrom || 'Anytime',
                preferredContactTimeTo: prevData.contactPreferences.preferredContactTimeTo || 'Anytime'
            }
        }));
    }, []);

    // Constants for form options
    const titleOptions = [
        { value: 'mr', label: 'Mr' },
        { value: 'mrs', label: 'Mrs' },
        { value: 'ms', label: 'Ms' },
        { value: 'miss', label: 'Miss' },
        { value: 'dr', label: 'Dr' }
    ];

    const loanPurposes = [
        { value: 'home', label: 'Home Purchase' },
        { value: 'refinance', label: 'Refinance' },
        { value: 'investment', label: 'Investment Property' },
        { value: 'car', label: 'Car/Vehicle' },
        { value: 'personal', label: 'Personal Loan' },
        { value: 'business', label: 'Business Loan' },
        { value: 'other', label: 'Other' }
    ];

    const loanTerms = [
        { value: '1', label: '1 Year' },
        { value: '2', label: '2 Years' },
        { value: '3', label: '3 Years' },
        { value: '5', label: '5 Years' },
        { value: '10', label: '10 Years' },
        { value: '15', label: '15 Years' },
        { value: '20', label: '20 Years' },
        { value: '25', label: '25 Years' },
        { value: '30', label: '30 Years' }
    ];

    const employmentStatus = [
        { value: 'fulltime', label: 'Full-time Employee' },
        { value: 'parttime', label: 'Part-time Employee' },
        { value: 'casual', label: 'Casual Employee' },
        { value: 'selfemployed', label: 'Self-employed' },
        { value: 'contractor', label: 'Contractor' },
        { value: 'pensioner', label: 'Pensioner/Retiree' },
        { value: 'unemployed', label: 'Currently Unemployed' }
    ];

    const contactMethods = [
        { value: 'Phone', label: 'Phone' },
        { value: 'SMS', label: 'SMS' },
        { value: 'Email', label: 'Email' }
    ];

    const contactHours = [
        { value: "Anytime", label: "Anytime" },
        { value: "9 AM", label: "9 AM" },
        { value: "10 AM", label: "10 AM" },
        { value: "11 AM", label: "11 AM" },
        { value: "12 PM", label: "12 PM" },
        { value: "1 PM", label: "1 PM" },
        { value: "2 PM", label: "2 PM" },
        { value: "3 PM", label: "3 PM" },
        { value: "4 PM", label: "4 PM" },
        { value: "5 PM", label: "5 PM" }
    ];

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        setSubmitAttempted(true);
        try {
            if (onSubmit) {
                await onSubmit(values);
                resetForm();
                setSubmitAttempted(false); // Reset after successful submission
            }
        } catch (error) {
            console.error('Error submitting application:', error);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="public-loan-application">
            <div className="container">
                <div className="application-header">
                    <h2 className="application-title">Quick Loan Application</h2>
                    <p className="application-subtitle">
                        Get started with your loan application in minutes. We'll contact you to discuss your requirements.
                        {referrerInfo && (
                            <span className="referrer-info">
                                <br />
                                <small>
                                    <strong>You were referred by: {referrerInfo.referrerDisplayName}</strong>
                                    {referrerInfo.description && (
                                        <>
                                            <br />
                                            {referrerInfo.description}
                                        </>
                                    )}
                                    <br />
                                    Your application will be attributed to this referrer for tracking purposes.
                                </small>
                            </span>
                        )}
                    </p>
                </div>

                <Formik
                    initialValues={applicationData}
                    validationSchema={PublicLoanApplicationValidationSchema}
                    enableReinitialize={true}
                    validateOnMount={false}
                    validateOnChange={true}
                    validateOnBlur={true}
                    onSubmit={(values, actions) => {
                        // This will be handled by our custom handleFormSubmit
                        // Just prevent the default Formik submission
                        actions.setSubmitting(false);
                    }}
                >
                    {({ errors, touched, setFieldValue, setFieldTouched, validateField, values, isSubmitting: formSubmitting, isValid, validateForm }) => {
                        // Helper function to determine if field should show error
                        const shouldShowError = (fieldError, fieldTouched) => {
                            // Show error if:
                            // 1. Field has error AND user has touched it, OR
                            // 2. Field has error AND form submit has been attempted
                            return fieldError && (fieldTouched || submitAttempted);
                        };

                        // Enhanced submit handler to validate all fields and show errors
                        const handleFormSubmit = async (e) => {
                            e.preventDefault();
                            setSubmitAttempted(true);
                            
                            // Validate all fields and get errors
                            const formErrors = await validateForm();
                            
                            // Mark all fields as touched to show errors
                            const touchAllFields = (obj, path = '') => {
                                Object.keys(obj).forEach(key => {
                                    const fieldPath = path ? `${path}.${key}` : key;
                                    if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
                                        touchAllFields(obj[key], fieldPath);
                                    } else {
                                        setFieldTouched(fieldPath, true);
                                    }
                                });
                            };
                            
                            if (Object.keys(formErrors).length > 0) {
                                // Touch all fields to show validation errors
                                touchAllFields(values);
                                console.log('Form has validation errors:', formErrors);
                                return;
                            }
                            
                            // If no errors, proceed with submission
                            try {
                                if (onSubmit) {
                                    await onSubmit(values);
                                    // Reset form state on successful submission
                                    setSubmitAttempted(false);
                                }
                            } catch (error) {
                                console.error('Error submitting application:', error);
                            }
                        };
                        
                        return (
                        <Form className="loan-application-form" onSubmit={handleFormSubmit}>
                            {/* Personal Details Section */}
                            <div className="form-section">
                                <h3 className="section-title">Personal Details</h3>
                                <div className="row">
                                    <div className="col-md-2 col-sm-12">
                                        <label className="form-label required">Title</label>
                                        <Field name="personalDetail.title">
                                            {({ field }) => (
                                                <div className={shouldShowError(errors.personalDetail?.title, touched.personalDetail?.title) ? 'error-field' : ''}
                                                     onClick={() => setFieldTouched("personalDetail.title", true)}>
                                                    <SelectField
                                                        fieldName="personalDetail.title"
                                                        defaultSelectedOption={
                                                            field.value 
                                                                ? titleOptions.find(opt => opt.value === field.value) || { value: '', label: 'Select...' }
                                                                : { value: '', label: 'Select...' }
                                                        }
                                                        options={titleOptions}
                                                        setFieldValue={setFieldValue}
                                                        callback={(fieldName, value) => {
                                                            setFieldTouched(fieldName, true);
                                                        }}
                                                    />
                                                </div>
                                            )}
                                        </Field>
                                        {errors.personalDetail?.title && (touched.personalDetail?.title || submitAttempted) && (
                                            <div className="error">{errors.personalDetail.title}</div>
                                        )}
                                    </div>

                                    <div className="col-md-5 col-sm-12">
                                        <label className="form-label required">First Name</label>
                                        <Field name="personalDetail.firstName">
                                            {({ field }) => (
                                                <input
                                                    type="text"
                                                    className={`form-control ${shouldShowError(errors.personalDetail?.firstName, touched.personalDetail?.firstName) ? 'error' : ''}`}
                                                    style={{ height: "35px", fontWeight: "400" }}
                                                    placeholder="Enter your first name"
                                                    value={field.value}
                                                    onChange={(e) => {
                                                        let value = e.target.value.replace(/[^a-zA-Z\s]/g, '');
                                                        setFieldValue("personalDetail.firstName", value);
                                                        setFieldTouched("personalDetail.firstName", true);
                                                    }}
                                                />
                                            )}
                                        </Field>
                                        {errors.personalDetail?.firstName && (touched.personalDetail?.firstName || submitAttempted) && (
                                            <div className="error">{errors.personalDetail.firstName}</div>
                                        )}
                                    </div>

                                    <div className="col-md-5 col-sm-12">
                                        <label className="form-label required">Last Name</label>
                                        <Field name="personalDetail.surname">
                                            {({ field }) => (
                                                <input
                                                    type="text"
                                                    className={`form-control ${shouldShowError(errors.personalDetail?.surname, touched.personalDetail?.surname) ? 'error' : ''}`}
                                                    style={{ height: "35px", fontWeight: "400" }}
                                                    placeholder="Enter your last name"
                                                    value={field.value}
                                                    onChange={(e) => {
                                                        let value = e.target.value.replace(/[^a-zA-Z\s]/g, '');
                                                        setFieldValue("personalDetail.surname", value);
                                                        setFieldTouched("personalDetail.surname", true);
                                                    }}
                                                />
                                            )}
                                        </Field>
                                        {errors.personalDetail?.surname && (touched.personalDetail?.surname || submitAttempted) && (
                                            <div className="error">{errors.personalDetail.surname}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-6">
                                        <label className="form-label required">Email Address</label>
                                        <Field name="personalDetail.email">
                                            {({ field, meta }) => (
                                                <div>
                                                    <input
                                                        type="email"
                                                        className={`form-control ${shouldShowError(errors.personalDetail?.email, touched.personalDetail?.email) ? 'error' : ''}`}
                                                        style={{ height: "35px", fontWeight: "400" }}
                                                        placeholder="your.email@example.com"
                                                        {...field}
                                                    />
                                                </div>
                                            )}
                                        </Field>
                                        {errors.personalDetail?.email && (touched.personalDetail?.email || submitAttempted) && (
                                            <div className="error">{errors.personalDetail.email}</div>
                                        )}
                                    </div>

                                    <div className="col-md-3">
                                        <label className="form-label required">Mobile Number</label>
                                        <Field name="personalDetail.mobileNumber">
                                            {({ field }) => (
                                                <input
                                                    type="tel"
                                                    className={`form-control ${shouldShowError(errors.personalDetail?.mobileNumber, touched.personalDetail?.mobileNumber) ? 'error' : ''}`}
                                                    style={{ height: "35px", fontWeight: "400" }}
                                                    placeholder="0412 345 678"
                                                    value={field.value}
                                                    onChange={(e) => {
                                                        setFieldValue("personalDetail.mobileNumber", e.target.value);
                                                        setFieldTouched("personalDetail.mobileNumber", true);
                                                    }}
                                                />
                                            )}
                                        </Field>
                                        {errors.personalDetail?.mobileNumber && (touched.personalDetail?.mobileNumber || submitAttempted) && (
                                            <div className="error">{errors.personalDetail.mobileNumber}</div>
                                        )}
                                    </div>

                                    <div className="col-md-3">
                                        <label className="form-label">Home Number</label>
                                        <Field name="personalDetail.homeNumber">
                                            {({ field }) => (
                                                <input
                                                    type="tel"
                                                    className="form-control"
                                                    style={{ height: "35px", fontWeight: "400" }}
                                                    placeholder="02 1234 5678"
                                                    value={field.value}
                                                    onChange={(e) => {
                                                        setFieldValue("personalDetail.homeNumber", e.target.value);
                                                        setFieldTouched("personalDetail.homeNumber", true);
                                                    }}
                                                />
                                            )}
                                        </Field>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-4">
                                        <label className="form-label required">Date of Birth</label>
                                        <Field name="personalDetail.dateOfBirth">
                                            {({ field }) => (
                                                <div className={shouldShowError(errors.personalDetail?.dateOfBirth, touched.personalDetail?.dateOfBirth || submitAttempted) ? 'error-field' : ''}>
                                                    <MaskedField
                                                        fieldName="personalDetail.dateOfBirth"
                                                        fieldValue={field.value}
                                                        maskRule="date"
                                                        fieldPlaceholder="dd/mm/yyyy"
                                                        setFieldValue={setFieldValue}
                                                        setFieldTouched={setFieldTouched}
                                                    />
                                                </div>
                                            )}
                                        </Field>
                                        {shouldShowError(errors.personalDetail?.dateOfBirth, touched.personalDetail?.dateOfBirth || submitAttempted) && (
                                            <div className="error">{errors.personalDetail.dateOfBirth}</div>
                                        )}
                                    </div>

                                    <div className="col-md-8">
                                        <label className="form-label required">Current Residential Address</label>
                                        <Field name="personalDetail.residentialAddress">
                                            {({ field }) => (
                                                <div className={`search-location-input ${shouldShowError(errors.personalDetail?.residentialAddress?.fullAddress, touched.personalDetail?.residentialAddress?.fullAddress || submitAttempted) ? 'error' : ''}`}>
                                                    <SearchLocationInput 
                                                        setAddressModule={(addressData) => {
                                                            setFieldValue("personalDetail.residentialAddress", addressData);
                                                        }}
                                                    />
                                                </div>
                                            )}
                                        </Field>
                                        {errors.personalDetail?.residentialAddress?.fullAddress && (touched.personalDetail?.residentialAddress?.fullAddress || submitAttempted) && (
                                            <div className="error">{errors.personalDetail.residentialAddress.fullAddress}</div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Loan Details Section */}
                            <div className="form-section">
                                <h3 className="section-title">Loan Details</h3>
                                <div className="row">
                                    <div className="col-md-4">
                                        <label className="form-label required">Loan Purpose</label>
                                        <Field name="loanDetail.purpose">
                                            {({ field }) => (
                                                <div className={shouldShowError(errors.loanDetail?.purpose, touched.loanDetail?.purpose) ? 'error-field' : ''}
                                                     onClick={() => setFieldTouched("loanDetail.purpose", true)}>
                                                    <SelectField
                                                        fieldName="loanDetail.purpose"
                                                        defaultSelectedOption={
                                                            field.value 
                                                                ? loanPurposes.find(opt => opt.value === field.value) || { value: '', label: 'Select...' }
                                                                : { value: '', label: 'Select...' }
                                                        }
                                                        options={loanPurposes}
                                                        setFieldValue={setFieldValue}
                                                        callback={(fieldName, value) => {
                                                            setFieldTouched(fieldName, true);
                                                        }}
                                                    />
                                                </div>
                                            )}
                                        </Field>
                                        {errors.loanDetail?.purpose && (touched.loanDetail?.purpose || submitAttempted) && (
                                            <div className="error">{errors.loanDetail.purpose}</div>
                                        )}
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label required">Loan Amount ($)</label>
                                        <Field name="loanDetail.amount">
                                            {({ field }) => (
                                                <input
                                                    type="number"
                                                    className={`form-control ${shouldShowError(errors.loanDetail?.amount, touched.loanDetail?.amount) ? 'error' : ''}`}
                                                    style={{ height: "35px", fontWeight: "400" }}
                                                    placeholder="50000"
                                                    value={field.value}
                                                    onChange={(e) => {
                                                        // Only allow numbers
                                                        const cleanValue = e.target.value.replace(/[^0-9]/g, '');
                                                        if (cleanValue === '') {
                                                            setFieldValue("loanDetail.amount", '');
                                                        } else {
                                                            const numValue = parseInt(cleanValue, 10);
                                                            console.log('Setting loan amount:', numValue, typeof numValue);
                                                            setFieldValue("loanDetail.amount", numValue);
                                                        }
                                                        setFieldTouched("loanDetail.amount", true);
                                                        // Force revalidation
                                                        setTimeout(() => validateField("loanDetail.amount"), 0);
                                                    }}
                                                />
                                            )}
                                        </Field>
                                        {errors.loanDetail?.amount && (touched.loanDetail?.amount || submitAttempted) && (
                                            <div className="error">{errors.loanDetail.amount}</div>
                                        )}
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label required">Loan Term</label>
                                        <Field name="loanDetail.term">
                                            {({ field }) => (
                                                <div className={shouldShowError(errors.loanDetail?.term, touched.loanDetail?.term) ? 'error-field' : ''}
                                                     onClick={() => setFieldTouched("loanDetail.term", true)}>
                                                    <SelectField
                                                        fieldName="loanDetail.term"
                                                        defaultSelectedOption={
                                                            field.value 
                                                                ? loanTerms.find(opt => opt.value === field.value) || { value: '', label: 'Select...' }
                                                                : { value: '', label: 'Select...' }
                                                        }
                                                        options={loanTerms}
                                                        setFieldValue={setFieldValue}
                                                        callback={(fieldName, value) => {
                                                            setFieldTouched(fieldName, true);
                                                        }}
                                                    />
                                                </div>
                                            )}
                                        </Field>
                                        {errors.loanDetail?.term && (touched.loanDetail?.term || submitAttempted) && (
                                            <div className="error">{errors.loanDetail.term}</div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Contact Preferences Section */}
                            <div className="form-section">
                                <h3 className="section-title">Contact Preferences</h3>
                                <div className="row">
                                    <div className="col-md-4">
                                        <label className="form-label">Preferred Contact Method</label>
                                        <Field name="contactPreferences.preferredContactMethod">
                                            {({ field }) => (
                                                <SelectField
                                                    fieldName="contactPreferences.preferredContactMethod"
                                                    defaultSelectedOption={
                                                        field.value 
                                                            ? contactMethods.find(opt => opt.value === field.value) || { value: 'Phone', label: 'Phone' }
                                                            : { value: 'Phone', label: 'Phone' }
                                                    }
                                                    options={contactMethods}
                                                    setFieldValue={setFieldValue}
                                                    callback={(fieldName, value) => {
                                                        setFieldTouched(fieldName, true);
                                                    }}
                                                />
                                            )}
                                        </Field>
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label">Preferred Contact Time From</label>
                                        <Field name="contactPreferences.preferredContactTimeFrom">
                                            {({ field }) => (
                                                <SelectField
                                                    fieldName="contactPreferences.preferredContactTimeFrom"
                                                    defaultSelectedOption={
                                                        field.value 
                                                            ? contactHours.find(opt => opt.value === field.value) || { value: 'Anytime', label: 'Anytime' }
                                                            : { value: 'Anytime', label: 'Anytime' }
                                                    }
                                                    options={contactHours}
                                                    setFieldValue={setFieldValue}
                                                    callback={(fieldName, value) => {
                                                        setFieldTouched(fieldName, true);
                                                    }}
                                                />
                                            )}
                                        </Field>
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label">Preferred Contact Time To</label>
                                        <Field name="contactPreferences.preferredContactTimeTo">
                                            {({ field }) => (
                                                <SelectField
                                                    fieldName="contactPreferences.preferredContactTimeTo"
                                                    defaultSelectedOption={
                                                        field.value 
                                                            ? contactHours.find(opt => opt.value === field.value) || { value: 'Anytime', label: 'Anytime' }
                                                            : { value: 'Anytime', label: 'Anytime' }
                                                    }
                                                    options={contactHours}
                                                    setFieldValue={setFieldValue}
                                                    callback={(fieldName, value) => {
                                                        setFieldTouched(fieldName, true);
                                                    }}
                                                />
                                            )}
                                        </Field>
                                    </div>
                                </div>
                            </div>

                            {/* Additional Information Section */}
                            <div className="form-section">
                                <h3 className="section-title">Additional Information</h3>
                                <div className="row">
                                    <div className="col-12">
                                        <label className="form-label">Additional Notes</label>
                                        <Field name="additionalInfo.notes">
                                            {({ field }) => (
                                                <TextArea
                                                    fieldName="additionalInfo.notes"
                                                    fieldValue={field.value}
                                                    setFieldValue={setFieldValue}
                                                    placeholder="Any additional information you'd like us to know..."
                                                />
                                            )}
                                        </Field>
                                    </div>
                                </div>
                            </div>

                            {/* Submit Section */}
                            <div className="form-section submit-section">
                                <div className="submit-info">
                                    <p>
                                        By submitting this application, you acknowledge that you have given consent for your contact details 
                                        to be passed on for the purpose of loan assessment and that our team will contact you to discuss your requirements.
                                    </p>
                                </div>
                                
                                {Object.keys(errors).length > 0 && submitAttempted && (
                                    <div className="error-summary">
                                        Please fix the highlighted errors above before submitting your application.
                                    </div>
                                )}

                                <div className="submit-button-container">
                                    <FormSubmissionButton 
                                        isActive={true} // Always enable the submit button
                                        buttonText={isSubmitting || formSubmitting ? "Submitting..." : "Submit Application"}
                                    />
                                </div>
                            </div>
                        </Form>
                        );
                    }}
                </Formik>
            </div>
        </div>
    );
};

export default PublicLoanApplication;